/* eslint-disable import/no-default-export */
import md5 from 'blueimp-md5';
// eslint-disable-next-line no-restricted-imports
import { Noun, Verb } from 'common/debug/analytics-constants';
import Cookie from 'js-cookie';
import moment from 'moment';
import Analytics from 'scripts/debug/classic-analytics';
import {
  AppCuesSkipEvent,
  AppCuesSubmitEvent,
  DismissedSurvey,
  DismissSurvey,
  GetDismissedSurveys,
  GetIdentifier,
} from './types';
const customError = require('@atlassian/trello-error-ext');

const BUCKETS = 1000;

const InvalidSurveyId = customError(
  'Survey:: surveyId is required and should be a string',
);
const InvalidAllocation = customError(
  'Survey:: allocation is required and should be a number between 0 and 1',
);

export default class Survey {
  surveyId: string;
  allocation: number;
  getDismissedSurveys: GetDismissedSurveys;
  dismissSurvey: DismissSurvey;
  getIdentifier: GetIdentifier;

  constructor(
    getDismissedSurveys: GetDismissedSurveys,
    dismissSurvey: DismissSurvey,
    getIdentifier: GetIdentifier,
    // left these params as optional while coffescript land still
    // uses it so that we can do validation
    surveyId?: string,
    allocation?: number,
  ) {
    if (typeof surveyId !== 'string') {
      throw InvalidSurveyId();
    }

    if (
      typeof allocation !== 'number' ||
      !(allocation <= 1 && allocation >= 0)
    ) {
      throw InvalidAllocation();
    }

    this.surveyId = surveyId;
    this.allocation = allocation;
    this.getDismissedSurveys = getDismissedSurveys;
    this.dismissSurvey = dismissSurvey;
    this.getIdentifier = getIdentifier;
  }

  show() {
    // early return for testing here. Rather than add this at the bottom,
    // keep it here to test whether this functionality works well
    if (this.hasDismissed() || this.hasSeenSurveyIn(14)) {
      return;
    }

    const forceSurveyCookie = Cookie.get('force_survey');

    if (forceSurveyCookie) {
      console.log(
        `${test.name} survey forced to show with "force_survey" cookie`,
      );

      return this.render();
    }

    if (this.isInBucket()) {
      this.render();
    }
  }

  get hashedId() {
    return parseInt(md5(this.surveyId + this.getIdentifier()).slice(0, 8), 16);
  }

  isInBucket() {
    const bucket = this.hashedId % BUCKETS;

    return bucket <= this.allocation * BUCKETS;
  }

  hasDismissed() {
    return !!this.getDismissedSurveys().find(
      (msg: DismissedSurvey) => msg.name.match(this.surveyId) !== null,
    );
  }

  hasSeenSurveyIn(days: number) {
    // see when the last survey was shown
    const lastDismissedSurvey = this.getDismissedSurveys()
      .sort(
        (a, b) =>
          new Date(a.lastDismissed).getTime() -
          new Date(b.lastDismissed).getTime(),
      )
      .slice(-1)[0];

    if (lastDismissedSurvey) {
      const daysSinceLastSurvey = moment().diff(
        moment(lastDismissedSurvey.lastDismissed),
        'days',
      );

      if (daysSinceLastSurvey < days) {
        return true;
      }
    }

    return false;
  }

  render() {
    const script = document.createElement('script');
    script.src = '//fast.appcues.com/26999.js';
    document.body.appendChild(script);

    const onScriptLoad = () => {
      window.Appcues.identify(this.getIdentifier());
      window.Appcues.show(this.surveyId);
      let isSubmitted = false;

      window.Appcues.on('flow_skipped', (data: AppCuesSkipEvent) => {
        // there can be multiple surveys on the page at once.
        if (data.flowId !== this.surveyId) {
          return;
        }

        if (!isSubmitted) {
          this.dismissSurvey(this.surveyId);
          Analytics.trackUE({
            category: Noun.SURVEY_MANAGER,
            verb: Verb.SKIPS,
            directObj: Noun.SURVEY_MANAGER,
            method: this.surveyId,
            context: {},
          });
        }
      });

      window.Appcues.on('form_submitted', (data: AppCuesSubmitEvent) => {
        // there can be multiple surveys on the page at once.
        if (data.flowId !== this.surveyId) {
          return;
        }

        isSubmitted = true;
        this.dismissSurvey(this.surveyId);
        for (const item of data.interaction.response) {
          Analytics.trackUE({
            category: Noun.SURVEY_MANAGER,
            verb: Verb.SUBMITS,
            directObj: Noun.SURVEY_MANAGER,
            method: this.surveyId,
            context: {
              fieldId: item.fieldId,
              label: item.label,
              value: item.value,
            },
          });
        }
      });
    };
    script.addEventListener('load', onScriptLoad.bind(this));
  }
}



// WEBPACK FOOTER //
// ./app/common/lib/survey/index.ts